import { useMutation } from "@apollo/client";
import { useEffect, useRef, useState } from "react";

import IntegrationDetailLayout from "@apps/org-settings/components/integrations/integration-detail-layout";
import { errorNotificationVar, successNotificationVar } from "@cache/cache";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import Input from "@components/input/input";

import { GetAccountQuery } from "../../../../types/graphql-schema";
import getAccountQuery from "../../graphql/get-account-query";
import removeAsanaIntegrationMutation from "../../graphql/remove-asana-integration-mutation";
import removeClickupIntegrationMutation from "../../graphql/remove-clickup-integration-mutation";
import removeGitHubIntegrationMutation from "../../graphql/remove-github-integration-mutation";
import removeHubspotIntegrationMutation from "../../graphql/remove-hubspot-integration-mutation";
import removeJiraIntegrationMutation from "../../graphql/remove-jira-integration-mutation";
import removePersonalTranscriptSyncCredentialMutation from "../../graphql/remove-personal-transcript-sync-credential-mutation";
import removeReadAiIntegrationMutation from "../../graphql/remove-read-ai-integration-mutation";
import removeSalesforceIntegrationMutation from "../../graphql/remove-salesforce-integration-mutation";
import removeZoomIntegrationMutation from "../../graphql/remove-zoom-integration-mutation";
import savePersonalTranscriptSyncCredentialMutation from "../../graphql/save-personal-transcript-sync-credential-mutation";
import { getPersonalIntegration } from "./integrations-config";

// Personal-integration status shows up in places backed by different queries:
// the account settings page (getAccount) and the homepage "Your Integrations"
// cards, which read the user from getLoggedInUser. Several personal OAuth
// connections (Jira, Salesforce, HubSpot, Read.AI) also create an
// OrganizationIntegration row that the homepage reads via
// getOrganizationIntegrations; disconnecting cascade-deletes that row, so we
// refetch that list too. Refetch all three so connect/disconnect updates
// everywhere.
const INTEGRATION_REFETCH_QUERIES = [
  getAccountQuery,
  "getLoggedInUser",
  "getOrganizationIntegrations",
];

type Account = NonNullable<GetAccountQuery["me"]>;

const PersonalIntegrationDetailPage = ({
  account,
  slug,
  open,
  onClose,
}: {
  account: Account;
  slug: string;
  open: boolean;
  onClose: () => void;
}) => {
  const definition = getPersonalIntegration(slug);
  const [apiKey, setApiKey] = useState("");
  const wasOpenRef = useRef(open);

  // Clear the API key field each time the dialog is (re)opened so a stale key
  // never lingers between sessions.
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setApiKey("");
    }
    wasOpenRef.current = open;
  }, [open]);

  const [removeZoom] = useMutation(removeZoomIntegrationMutation, {
    refetchQueries: INTEGRATION_REFETCH_QUERIES,
  });
  const [removeAsana] = useMutation(removeAsanaIntegrationMutation, {
    refetchQueries: INTEGRATION_REFETCH_QUERIES,
  });
  const [removeGitHub] = useMutation(removeGitHubIntegrationMutation, {
    refetchQueries: INTEGRATION_REFETCH_QUERIES,
  });
  const [removeJira] = useMutation(removeJiraIntegrationMutation, {
    refetchQueries: INTEGRATION_REFETCH_QUERIES,
  });
  const [removeClickup] = useMutation(removeClickupIntegrationMutation, {
    refetchQueries: INTEGRATION_REFETCH_QUERIES,
  });
  const [removeSalesforce] = useMutation(removeSalesforceIntegrationMutation, {
    refetchQueries: INTEGRATION_REFETCH_QUERIES,
  });
  const [removeHubspot] = useMutation(removeHubspotIntegrationMutation, {
    refetchQueries: INTEGRATION_REFETCH_QUERIES,
  });
  const [removeReadAi] = useMutation(removeReadAiIntegrationMutation, {
    refetchQueries: INTEGRATION_REFETCH_QUERIES,
  });
  const [removeTranscriptToken] = useMutation(
    removePersonalTranscriptSyncCredentialMutation,
    { refetchQueries: INTEGRATION_REFETCH_QUERIES },
  );
  const [saveTranscriptToken, { loading: isSavingToken }] = useMutation(
    savePersonalTranscriptSyncCredentialMutation,
    { refetchQueries: INTEGRATION_REFETCH_QUERIES },
  );

  if (!definition) {
    return (
      <IntegrationDetailLayout
        open={open}
        onClose={onClose}
        name="Integration not found"
        logo=""
      >
        <p className="text-sm text-gray-500">
          We couldn’t find an integration with the slug “{slug}”.
        </p>
      </IntegrationDetailLayout>
    );
  }

  const isConnected = definition.isConnected(account);

  const handleConnect = () => {
    if (definition.connect.kind === "url") {
      window.location.href = definition.connect.url;
    }
  };

  const handleDisconnect = () => {
    if (definition.connect.kind === "transcriptToken") {
      removeTranscriptToken({
        variables: { provider: definition.connect.provider },
        onCompleted: () => {
          successNotificationVar({
            title: `${definition.name} API key removed`,
          });
          onClose();
        },
        onError: (error) => {
          errorNotificationVar({ title: error.message });
        },
      });
      return;
    }
    const onCompleted = () => {
      successNotificationVar({ title: `${definition.name} disconnected` });
      onClose();
    };
    switch (definition.slug) {
      case "zoom":
        removeZoom({ onCompleted });
        return;
      case "asana":
        removeAsana({ onCompleted });
        return;
      case "github":
        removeGitHub({ onCompleted });
        return;
      case "jira":
        removeJira({ onCompleted });
        return;
      case "clickup":
        removeClickup({ onCompleted });
        return;
      case "salesforce":
        removeSalesforce({ onCompleted });
        return;
      case "hubspot":
        removeHubspot({ onCompleted });
        return;
      case "read-ai":
        removeReadAi({ onCompleted });
        return;
    }
  };

  const handleSaveTranscriptToken = () => {
    if (definition.connect.kind !== "transcriptToken") {
      return;
    }
    saveTranscriptToken({
      variables: {
        provider: definition.connect.provider,
        apiKey: apiKey.trim(),
      },
      onCompleted: () => {
        successNotificationVar({
          title: `${definition.name} API key saved`,
        });
        onClose();
      },
      onError: (error) => {
        errorNotificationVar({ title: error.message });
      },
    });
  };

  const isTranscriptToken = definition.connect.kind === "transcriptToken";

  const actions = isTranscriptToken ? (
    <>
      {isConnected && (
        <Button theme={buttonTheme.textRed} onClick={handleDisconnect}>
          Disconnect
        </Button>
      )}
      <Button
        theme={buttonTheme.primary}
        onClick={handleSaveTranscriptToken}
        disabled={isSavingToken || !apiKey.trim()}
      >
        {isConnected ? "Save" : "Connect"}
      </Button>
    </>
  ) : isConnected ? (
    <Button theme={buttonTheme.textRed} onClick={handleDisconnect}>
      Disconnect
    </Button>
  ) : (
    <Button theme={buttonTheme.primary} onClick={handleConnect}>
      Connect
    </Button>
  );

  return (
    <IntegrationDetailLayout
      open={open}
      onClose={onClose}
      name={definition.name}
      logo={definition.logo}
      description={definition.description}
      statusLabel={isConnected ? "Enabled" : "Not connected"}
      statusTone={isConnected ? "enabled" : "neutral"}
      actions={actions}
    >
      {definition.connect.kind === "transcriptToken" && (
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Enter your personal {definition.name} API key. Topicflow stores it
            for your active organizations and uses it to import your meeting
            transcripts.
          </p>
          <Input
            autoFocus
            label="API key"
            name={`${definition.connect.provider}-api-key`}
            type="password"
            value={apiKey}
            onChange={(event) => setApiKey(event.target.value)}
            autoComplete="off"
          />
        </div>
      )}
    </IntegrationDetailLayout>
  );
};

export default PersonalIntegrationDetailPage;
